
import React, { useState } from 'react';
import { Wallet, Eye, X, PieChart, Landmark } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../Config';

export default function SocioCuentas({ cuentas, formatCurrency }) {
    const { token } = useAuth();

    // History Modal State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const fetchHistory = async (cuentaId) => {
        setLoadingHistory(true);
        try {
            const response = await fetch(`${API_BASE_URL}/cuentas/${cuentaId}/transacciones`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error al cargar historial');
            const data = await response.json();
            setHistory(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenHistory = (cuenta) => {
        setSelectedAccount(cuenta);
        setHistory([]);
        setShowHistoryModal(true);
        fetchHistory(cuenta.id);
    };

    return (
        <div className="animate-premium-in space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-1.5 h-8 bg-slate-900 rounded-full" />
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tighter uppercase">Cuentas y Productos Bancarios</h2>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Detalle técnico de cuentas vinculadas al socio</p>
                    </div>
                </div>
            </div>

            {(!cuentas || cuentas.length === 0) ? (
                <div className="enterprise-card p-12 text-center border-dashed border-2 bg-slate-50/50 border-slate-200">
                    <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-4 opacity-50" />
                    <p className="text-slate-400 font-black text-[10px] uppercase tracking-widest">Sin productos financieros activos</p>
                </div>
            ) : (
                <div className="enterprise-card p-0 overflow-hidden border-2 border-slate-100 shadow-xl shadow-slate-200/50">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-800 text-white">
                            <tr>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">Producto / Tipo</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest">Número de Cuenta</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-right">Saldo Actual</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-right">Saldo Bloqueado</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-right">Disponible</th>
                                <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-center w-24">Acción</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {cuentas.map((cuenta) => (
                                <tr key={cuenta.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-100 rounded-lg text-slate-400 group-hover:bg-uni-teal group-hover:text-white transition-all">
                                                <Landmark className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-black text-slate-800 tracking-tight">{cuenta.tipoCuenta?.descripcion}</div>
                                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{cuenta.moneda?.descripcion}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-mono font-black text-xs text-slate-600">
                                        {cuenta.tipoCuenta?.codigo} {cuenta.numeroCuenta}
                                    </td>
                                    <td className="px-6 py-4 text-right font-black text-sm text-slate-700">
                                        {formatCurrency(cuenta.saldoActual || 0)}
                                    </td>
                                    <td className="px-6 py-4 text-right font-black text-sm text-rose-500">
                                        {formatCurrency(cuenta.montoBloqueado || 0)}
                                    </td>
                                    <td className="px-6 py-4 text-right font-black text-base text-emerald-600 bg-emerald-50/20">
                                        {formatCurrency(cuenta.saldoDisponible || 0)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleOpenHistory(cuenta)}
                                            className="w-10 h-10 flex items-center justify-center bg-slate-900 text-white rounded-xl hover:bg-uni-teal transition-all shadow-lg active:scale-90"
                                            title="Extracto"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-slate-50">
                            <tr>
                                <td colSpan="4" className="px-6 py-4 text-right text-[10px] font-black uppercase text-slate-400">Total Disponibilidad Consolidada</td>
                                <td className="px-6 py-4 text-right font-black text-xl text-uni-teal bg-uni-teal/5">
                                    {formatCurrency(cuentas.reduce((acc, curr) => acc + (curr.saldoDisponible || 0), 0))}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* --- HISTORY MODAL (CUENTAS) --- */}
            {showHistoryModal && selectedAccount && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-premium-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
                        <div className="bg-slate-900 text-white px-8 py-6 flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                                    <PieChart className="w-6 h-6 text-uni-teal" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black tracking-tight uppercase">Extracto de Movimientos</h3>
                                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                                        {selectedAccount.tipoCuenta?.descripcion} • {selectedAccount.tipoCuenta?.codigo} {selectedAccount.numeroCuenta}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setShowHistoryModal(false)} className="hover:bg-rose-500 p-2 rounded-xl transition-all">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto p-8">
                            {loadingHistory ? (
                                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                    <div className="animate-spin h-8 w-8 border-4 border-uni-teal border-t-transparent rounded-full mb-4"></div>
                                    <p className="text-[9px] font-black uppercase tracking-widest">Sincronizando...</p>
                                </div>
                            ) : history.length === 0 ? (
                                <div className="text-center py-20 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-100">
                                    <p className="text-[10px] font-black text-slate-400 uppercase">Sin transacciones registradas</p>
                                </div>
                            ) : (
                                <div className="enterprise-card p-0 border-none shadow-none">
                                    <table className="w-full">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="px-6 py-3 text-[9px] font-black uppercase text-slate-400">Fecha</th>
                                                <th className="px-6 py-3 text-[9px] font-black uppercase text-slate-400">Concepto</th>
                                                <th className="px-6 py-3 text-[9px] font-black uppercase text-slate-400 text-right">Monto</th>
                                                <th className="px-6 py-3 text-[9px] font-black uppercase text-slate-400 text-right">Saldo Final</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {history.map((tx) => (
                                                <tr key={tx.id} className="hover:bg-slate-50/50">
                                                    <td className="px-6 py-4">
                                                        <span className="font-bold text-slate-800 text-xs">{new Date(tx.fecha).toLocaleDateString()}</span>
                                                        <span className="block text-[8px] font-black text-slate-300 uppercase mt-0.5">{new Date(tx.fecha).toLocaleTimeString()}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="font-black text-slate-800 block text-xs tracking-tight">{tx.concepto}</span>
                                                        <span className="text-[8px] text-slate-400 font-black uppercase tracking-widest">Ref: {tx.referencia || 'N/A'}</span>
                                                    </td>
                                                    <td className={`px-6 py-4 text-right font-black text-xs ${['DB'].includes(tx.tipoMovimiento?.codigo) ? 'text-rose-500' : 'text-emerald-500'}`}>
                                                        {['DB'].includes(tx.tipoMovimiento?.codigo) ? '-' : '+'}
                                                        {formatCurrency(tx.monto)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-slate-900 font-black text-xs">
                                                        {formatCurrency(tx.saldoPosterior)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
